import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { AddIcon } from '../../../icons/Icons'
import { DeviceIcon } from 'src/Libs/flow/icons/DeviceIcon'
import { getDeviceDefaultName } from '../../../parsers/device-parsers/get-device-default-name'
import {
  deleteSavedDeviceFromInstallation,
  removeUnsavedDeviceFromInstallation,
} from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { logError } from '../../../utils/logger'
import { DevicesTableDesktop } from './DevicesTableDesktop'
import { DevicesTableMobile } from './DevicesTableMobile'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationForm, useInstallationFormDevices } from 'src/Libs/flow/installation/hooks'
import { useAddDeviceDialog } from '../../hooks/use-add-device-dialog'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

export const DevicesTable: React.FC = () => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  const { getValues, setValue, reset } = useInstallationForm()
  const devices = useInstallationFormDevices()

  const openAddDeviceDialog = useAddDeviceDialog()

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<{ [id: string]: true }>({})
  const resetSelectedDevices = (): void => setSelectedDeviceIds({})

  const numberOfDevices = devices.length

  useEffect(() => {
    resetSelectedDevices()
  }, [numberOfDevices])

  const toggleDeviceSelection = (id: string): void => {
    if (selectedDeviceIds[id]) {
      const { [id]: _, ...otherSelectedDeviceIds } = selectedDeviceIds
      setSelectedDeviceIds(otherSelectedDeviceIds)
    } else {
      setSelectedDeviceIds({ ...selectedDeviceIds, [id]: true })
    }
  }

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()
  const handleDelete = (): void => {
    const devicesToDelete = devices.filter(({ id }) => !!selectedDeviceIds[id])
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      children: (
        <>
          {devicesToDelete.map(device => (
            <Box key={device.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <DeviceIcon device={device.deviceCategory} fontSize="large" />
              </Box>
              <Typography>{getDeviceDefaultName(device.name, device.deviceCategory, device.deviceId, t)}</Typography>
            </Box>
          ))}
        </>
      ),
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        onClose()
        resetSelectedDevices()

        const savedDevices = devicesToDelete.filter(device => !!device.deviceId)
        const unsavedDevices = devicesToDelete.filter(device => !device.deviceId)

        unsavedDevices.forEach(device => removeUnsavedDeviceFromInstallation(device, getValues, setValue))

        if (!savedDevices.length) return

        const installationId = getValues('id')
        if (!installationId) {
          logError('No installation ID. Can not delete devices.')
          return
        }

        const requests = savedDevices.flatMap(device => {
          const request = deleteSavedDeviceFromInstallation(device, getValues, t, authFetch)
          if (!request) return []
          return [request]
        })

        Promise.resolve().then(() => {
          openInstallationDeleteDialog({
            installationId,
            requests,
            reset,
            shouldSync: !!getValues('emcDevice.pairingId'),
          })
        })
      },
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', px: 2 }}>
      {upMd ? (
        <DevicesTableDesktop
          devices={devices}
          selectedDeviceIds={selectedDeviceIds}
          toggleDeviceSelection={toggleDeviceSelection}
          handleDelete={handleDelete}
        />
      ) : (
        <DevicesTableMobile
          devices={devices}
          selectedDeviceIds={selectedDeviceIds}
          toggleDeviceSelection={toggleDeviceSelection}
          handleDelete={handleDelete}
        />
      )}

      <Button
        startIcon={<AddIcon />}
        size="large"
        onClick={() => openAddDeviceDialog(null)}
        sx={{ my: 2, visibility: canEdit ? undefined : 'hidden', alignSelf: 'flex-end' }}
      >
        {t('installation.addComponent')}
      </Button>
    </Box>
  )
}
